import React from 'react';

import { BaseApiResponse } from '@lumapps/base-api';
import { NavigationMap } from '@lumapps/navigation/types';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { Action } from '@lumapps/redux';
import { GLOBAL, withApiv1Languages } from '@lumapps/translations';
import { logException } from '@lumapps/utils/log/logException';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { actions, reducer, initialState, NavigationAdminState } from '../ducks/navigationAdminSlice';

export interface UseNavigationAdminProps {
    /** The api call to get the navigation Map */
    getNavigationMap: () => Promise<BaseApiResponse<NavigationMap>>;
    /** The api call to update the navigation Map */
    updateNavigationMap: (newNavigationMap: NavigationMap) => Promise<BaseApiResponse<NavigationMap>>;
    /** Whether the navigation should be fetched when the hook is mounted */
    fetchOnMount?: boolean;
}

/** the props returned by the hook */
interface UseNavigationAdminApi extends NavigationAdminState {
    /** The load data function to call */
    loadData(): void;
    /**
     * The save function to call.
     * An optional new map and callback can be given to customize the behavior while
     * keeping the general state management.
     * By default, the map stored in the state will be saved. */
    onSave(newMap?: NavigationMap, onSaveSuccess?: (savedMap: NavigationMap) => void): void;
    /** */
    dispatch: React.Dispatch<
        Action<
            | 'setNavigationMap'
            | 'setLoadingState'
            | 'setIsEditing'
            | 'deleteNode'
            | 'addNode'
            | 'replaceNodesAtPath'
            | 'editNode'
            | 'setChangedStatus'
        >
    >;
}

export const useNavigationAdmin = ({
    getNavigationMap,
    updateNavigationMap,
    fetchOnMount = true,
}: UseNavigationAdminProps): UseNavigationAdminApi => {
    const [state, dispatch] = React.useReducer(reducer, initialState);

    const { error, success } = useNotification();

    const loadData = React.useCallback(async () => {
        dispatch(actions.setLoadingState(BaseLoadingStatus.loading));

        try {
            const response = await getNavigationMap();

            /**
             * When regional languages (e.g. pt-BR, zh-TW) are set, make sure to convert them into v1 format
             * (pt_br, zh_tw) so it works with the multilang components and the rest of app
             */
            dispatch(actions.saveNavigationMap(withApiv1Languages(response.data)));
        } catch (exception) {
            logException(exception);
            dispatch(actions.setLoadingState(BaseLoadingStatus.error));
            error({ translate: GLOBAL.GENERIC_ERROR });
        }
    }, [error, getNavigationMap]);

    // Load data on mount
    React.useEffect(() => {
        if (fetchOnMount) {
            loadData();
        }
    }, [fetchOnMount, loadData]);

    const onSave: UseNavigationAdminApi['onSave'] = React.useCallback(
        (newMap, onSaveSuccess) => {
            const { navigationMap: stateNavigationMap, loadingState } = state;
            /** Use the given map if set, if not use the map stored in state  */
            const navigationMap = newMap && newMap?.id ? newMap : stateNavigationMap;

            if (navigationMap !== undefined && loadingState !== BaseLoadingStatus.saving) {
                dispatch(actions.setLoadingState(BaseLoadingStatus.saving));

                const saveMap = async () => {
                    try {
                        const { data } = await updateNavigationMap(navigationMap);

                        /**
                         * When regional languages (e.g. pt-BR, zh-TW) are set, make sure to convert them into v1 format
                         * (pt_br, zh_tw) so it works with the multilang components and the rest of app
                         */
                        const newNavigationMap = withApiv1Languages(data);

                        if (newNavigationMap) {
                            // update the editable navigationMap with the new NavigationMap returned from backend
                            dispatch(actions.saveNavigationMap(newNavigationMap));

                            success({ translate: GLOBAL.SAVED });
                            if (onSaveSuccess) {
                                onSaveSuccess(newNavigationMap);
                            }
                        }
                    } catch (exception) {
                        logException(exception);
                        dispatch(actions.setLoadingState(BaseLoadingStatus.idle));
                        error({ translate: GLOBAL.GENERIC_ERROR });
                    }
                };

                saveMap();
            }
        },
        [error, state, success, updateNavigationMap],
    );

    return {
        loadData,
        onSave,
        dispatch,
        ...state,
    };
};
